import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const TIMELINE_OPTIONS = ['ASAP', 'Within 2 weeks', 'Within a month', 'Just exploring'];

const hStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 800,
  color: 'var(--bw-dark)',
  letterSpacing: '-0.025em',
  lineHeight: 1.15,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  border: '1.5px solid var(--bw-border)',
  borderRadius: '10px',
  backgroundColor: 'var(--bw-white)',
  color: 'var(--bw-dark)',
  fontFamily: 'var(--font-body)',
  fontSize: '15px',
  outline: 'none',
  transition: 'border-color 0.15s',
};

const SquareOneAlumniApply = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', sq1_mentor: '', sq1_mentor_email: '',
    sq1_cohort: '', project_description: '', timeline: '', additional_info: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');
    if (formData.project_description.length < 50) {
      setStatus('error');
      setErrorMessage('Please describe your project in at least 50 characters so we can understand what you need built.');
      return;
    }
    try {
      const response = await fetch('/api/sq1-applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Failed to submit application');
      setStatus('success');
      setFormData({ name: '', email: '', sq1_mentor: '', sq1_mentor_email: '', sq1_cohort: '', project_description: '', timeline: '', additional_info: '' });
    } catch (error) {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again or email us at hello@brightwings.io.');
      console.error('SQ1 application submission error:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const focusStyle = { borderColor: 'var(--bw-blue)' };
  const blurStyle = { borderColor: 'var(--bw-border)' };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bw-white)' }}>
      <Navigation />

      {/* Hero */}
      <section className="bw-hero">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bw-eyebrow-pill">SQ1 Alumni Program</div>
            <h1 style={{ ...hStyle, fontSize: 'clamp(36px, 5.5vw, 68px)', marginBottom: '20px' }}>
              Apply <em style={{ fontStyle: 'normal', color: 'var(--bw-blue)' }}>Now.</em>
            </h1>
            <p style={{ fontSize: 'clamp(17px, 2vw, 21px)', color: 'var(--bw-dark)', fontWeight: 500, marginBottom: '12px' }}>
              One free MVP build per year (valued at $10K).
            </p>
            <p style={{ fontSize: '15px', color: 'var(--bw-text-mid)' }}>
              <Link to="/squareone-alumni" style={{ color: 'var(--bw-blue)', fontWeight: 600, textDecoration: 'underline' }}>
                Learn more about the program
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="bw-section-surface">
        <div className="max-w-2xl mx-auto">
          <div className="bw-card">
            {status === 'success' ? (
              <div className="bw-card-body text-center py-12">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'var(--bw-blue-light)' }}>
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--bw-blue)' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 style={{ ...hStyle, fontSize: '22px', marginBottom: '12px' }}>Application received!</h3>
                <p style={{ color: 'var(--bw-text-mid)', marginBottom: '32px', lineHeight: 1.6 }}>
                  Thank you for applying. We'll review your application and reach out within one week if we think there's a fit.
                </p>
                <Link to="/squareone-alumni" onClick={() => window.scrollTo(0, 0)} className="bw-btn-secondary">
                  Back to program details
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bw-card-body space-y-5">
                {[
                  { id: 'name', label: 'Name *', type: 'text', placeholder: 'Your full name', minLength: 2 },
                  { id: 'email', label: 'Email *', type: 'email', placeholder: 'you@email.com' },
                  { id: 'sq1_mentor', label: 'SQ1 Mentor Name *', type: 'text', placeholder: 'Name of your SQ1 bootcamp mentor', minLength: 2 },
                  { id: 'sq1_mentor_email', label: 'SQ1 Mentor Email *', type: 'email', placeholder: 'mentor@email.com' },
                  { id: 'sq1_cohort', label: 'SQ1 Cohort / Completion Date *', type: 'text', placeholder: 'e.g. Fall 2025', minLength: 3 },
                ].map((field) => (
                  <div key={field.id}>
                    <label htmlFor={field.id} className="block text-sm font-medium mb-2" style={{ color: 'var(--bw-text-mid)' }}>
                      {field.label}
                    </label>
                    <input
                      type={field.type}
                      id={field.id}
                      name={field.id}
                      required
                      minLength={field.minLength}
                      value={formData[field.id as keyof typeof formData]}
                      onChange={handleChange}
                      style={inputStyle}
                      placeholder={field.placeholder}
                      onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle)}
                      onBlur={(e) => Object.assign(e.currentTarget.style, blurStyle)}
                    />
                  </div>
                ))}

                <div>
                  <label htmlFor="project_description" className="block text-sm font-medium mb-2" style={{ color: 'var(--bw-text-mid)' }}>
                    What do you need built? *
                  </label>
                  <textarea
                    id="project_description"
                    name="project_description"
                    required
                    rows={5}
                    value={formData.project_description}
                    onChange={handleChange}
                    style={{ ...inputStyle, resize: 'none' }}
                    placeholder="Describe your project: what problem does it solve and who is it for? (50 characters minimum)"
                    onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle)}
                    onBlur={(e) => Object.assign(e.currentTarget.style, blurStyle)}
                  />
                </div>

                <div>
                  <label htmlFor="timeline" className="block text-sm font-medium mb-2" style={{ color: 'var(--bw-text-mid)' }}>
                    When do you need to start? *
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    required
                    value={formData.timeline}
                    onChange={handleChange}
                    style={{ ...inputStyle, color: formData.timeline ? 'var(--bw-dark)' : 'var(--bw-muted)' }}
                    onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle)}
                    onBlur={(e) => Object.assign(e.currentTarget.style, blurStyle)}
                  >
                    <option value="" disabled>Select a timeline</option>
                    {TIMELINE_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="additional_info" className="block text-sm font-medium mb-2" style={{ color: 'var(--bw-text-mid)' }}>
                    Anything else?
                  </label>
                  <textarea
                    id="additional_info"
                    name="additional_info"
                    rows={3}
                    value={formData.additional_info}
                    onChange={handleChange}
                    style={{ ...inputStyle, resize: 'none' }}
                    placeholder="Tech stack preferences, constraints, additional context (optional)"
                    onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle)}
                    onBlur={(e) => Object.assign(e.currentTarget.style, blurStyle)}
                  />
                </div>

                {status === 'error' && (
                  <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
                    <p className="text-sm" style={{ color: '#DC2626' }}>{errorMessage}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="bw-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  {status === 'submitting' ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    'Submit Application'
                  )}
                </button>

                <p className="text-xs text-center" style={{ color: 'var(--bw-muted)' }}>
                  Applications are reviewed on a rolling basis. One free build awarded per year.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SquareOneAlumniApply;
